import {
    CenterFocusWeakOutlined,
    Close,
    EditOutlined,
    InfoOutlined,
    MoreVert,
} from '@mui/icons-material';
import { Box, Typography } from '@mui/material';
import {
    useEffect,
    useRef,
    useState,
    type MouseEvent,
    type ReactNode,
} from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import {
    CardHeaderAction,
    DetailsCard,
    DetailsCardHeader,
} from '~/components/DetailsCard';
import InlineEdit from '~/components/InlineEdit';
import { besideSidebarPoint } from '~/components/Properties/PropertiesSidePanel';
import StructureColorButton from '~/components/Structures/StructureColorButton';
import { isApprovalStatusEditable } from '~/domain/dicom/dicomConstants';
import { RoiReadModel } from '~/domain/readmodels/StructureSetReadModel';
import { useRoiDetailView } from '~/hooks/domain/useRoiDetailView';
import useDoseTargetLookup from '~/hooks/patient/dose/useDoseTargetLookup';
import useRoi from '~/hooks/structure-set/useRoi';
import useRoiDeselect from '~/hooks/structure-set/useRoiDeselect';
import useRoiRename from '~/hooks/structure-set/useRoiRename';
import useStructureNameValidation from '~/hooks/structure-set/useStructureNameValidation';
import { useSidePropertiesPanel } from '~/hooks/useSidePropertiesPanel';
import useViewportFocus from '~/hooks/useViewportFocus';
import StructureInformation from './StructureInformation';
import StructureMoreMenu from './StructureMoreMenu';
import StructurePropertiesPanel from './StructurePropertiesPanel';
import StructureTypeMenu from './StructureTypeMenu';

interface StructureDetailsProps {
    roi: RoiReadModel;
    /** Shown at the end of the card, under the structure's lines. */
    children?: ReactNode;
}

/**
 * The card at the bottom of the structure list for the selected structure:
 * its colour button, its name and the actions Rename, Center on Structure,
 * Properties, More and Deselect, then its type, approval status, volume,
 * generation algorithm, identification code and, for a point, its
 * coordinates. The More menu, the Type menu and Structure Properties open
 * beside the sidebar, one at a time. Deselecting the structure, or leaving
 * the card, closes Structure Properties.
 */
export default function StructureDetails({
    roi,
    children,
}: StructureDetailsProps) {
    const intl = useIntl();
    const {
        setInterpretedType,
        setPointCoordinates,
        getRoiVolumeCm3: legacyGetRoiVolumeCm3,
        getRoiEquivalentSphereDiameterCm: legacyGetEquivalentSphereDiameterCm,
        isRoiEmpty,
        setVisible,
        setElectronDensityOverride,
        setMassDensityOverride,
    } = useRoi(roi);
    const detailView = useRoiDetailView(roi.roiId);
    const { focusOnStructure, focusOnPoint } = useViewportFocus();
    const deselect = useRoiDeselect(roi);
    const validateName = useStructureNameValidation(roi);
    const isTarget = useDoseTargetLookup().has(roi.name);

    const cardRef = useRef<HTMLDivElement>(null);
    const [isRenaming, setIsRenaming] = useState(false);
    const [menu, setMenu] = useState<{
        kind: 'more' | 'type';
        opener: HTMLElement;
    }>();
    const {
        isOpen: propertiesOpen,
        open: openProperties,
        close: closeProperties,
    } = useSidePropertiesPanel('structure');
    // Leaving the card, which deselecting the structure does, closes it.
    useEffect(() => closeProperties, [closeProperties]);

    const structureCanBeEdited =
        detailView !== undefined
            ? detailView.isEditable
            : isApprovalStatusEditable(roi.approvalStatus);
    const hasContourData = !isRoiEmpty();
    // volumeCm3 from the projection (already mm³÷1000); legacy path as fallback.
    const volumeCm3: number =
        detailView?.volumeCm3 != null
            ? detailView.volumeCm3
            : legacyGetRoiVolumeCm3();
    const equivalentSphereDiameterCm: number =
        detailView !== undefined
            ? 2 * Math.cbrt((3 * volumeCm3) / (4 * Math.PI))
            : legacyGetEquivalentSphereDiameterCm();

    const setName = useRoiRename(roi.roiId);

    const openMenu =
        (kind: 'more' | 'type') => (event: MouseEvent<HTMLElement>) => {
            const opener = event.currentTarget;
            closeProperties();
            setMenu((current) =>
                current?.kind === kind ? undefined : { kind, opener }
            );
        };
    const closeMenu = () => setMenu(undefined);
    const menuPosition = (kind: 'more' | 'type') =>
        menu?.kind === kind && cardRef.current
            ? besideSidebarPoint(cardRef.current)
            : undefined;

    const toggleProperties = () => {
        setMenu(undefined);
        if (propertiesOpen) {
            closeProperties();
        } else {
            openProperties();
        }
    };

    const handleFocus = () => {
        // Focusing on a hidden ROI shows nothing useful, so flip visibility
        // on, as the row's focus action does. Only once the edit target
        // actually resolved though: an unresolved target must not force
        // visibility behind a focus that silently never happens.
        const focused =
            roi.isPointRoi && roi.pointCoordinatesMm
                ? focusOnPoint(roi.pointCoordinatesMm)
                : focusOnStructure(roi.roiId);
        if (focused && !roi.visible) {
            setVisible(true);
        }
    };

    const label = (id: string) => intl.formatMessage({ id });

    return (
        <DetailsCard ref={cardRef} data-testid="structure-details-card">
            <DetailsCardHeader>
                <Box display="flex" flexShrink={0}>
                    <StructureColorButton
                        roi={roi}
                        source="footer"
                        anchorFallbackRef={cardRef}
                        size={14}
                        isEmptyVolume={!hasContourData}
                        isMarker={roi.isPointRoi}
                        isTarget={isTarget}
                    />
                </Box>
                <Box flex={1} minWidth={0} mr={0.5} ml={0.5}>
                    {isRenaming ? (
                        <InlineEdit
                            value={roi.name}
                            onSubmit={setName}
                            onCancel={() => setIsRenaming(false)}
                            validate={validateName}
                            fontSize={14}
                            fontWeight={500}
                        />
                    ) : (
                        <Typography
                            fontSize={14}
                            fontWeight={500}
                            noWrap
                            title={roi.name}
                            data-testid={`structure-details-name-${roi.name}`}
                            onDoubleClick={
                                structureCanBeEdited
                                    ? () => setIsRenaming(true)
                                    : undefined
                            }
                            sx={{
                                cursor: structureCanBeEdited
                                    ? 'pointer'
                                    : undefined,
                            }}
                        >
                            {roi.name}
                        </Typography>
                    )}
                </Box>
                <Box display="flex" mr="-3px">
                    <CardHeaderAction
                        Icon={EditOutlined}
                        label={label('structure.rename')}
                        onClick={() => setIsRenaming(true)}
                        disabled={!structureCanBeEdited}
                        disabledTooltip={
                            <FormattedMessage id="structure.approved" />
                        }
                        data-testid={`structure-edit-button-${roi.name}`}
                    />
                </Box>
                <CardHeaderAction
                    Icon={CenterFocusWeakOutlined}
                    label={label('structure.focus')}
                    onClick={handleFocus}
                    data-testid={`structure-details-focus-button-${roi.name}`}
                />
                <CardHeaderAction
                    Icon={InfoOutlined}
                    label={label('properties')}
                    onClick={toggleProperties}
                    selected={propertiesOpen}
                    data-testid={`structure-info-button-${roi.name}`}
                />
                <Box display="flex" mx={-0.5}>
                    <CardHeaderAction
                        Icon={MoreVert}
                        label={label('moreActions')}
                        onClick={openMenu('more')}
                        selected={menu?.kind === 'more'}
                        opensMenu
                        data-testid="structure-more-menu-button"
                    />
                </Box>
                <CardHeaderAction
                    Icon={Close}
                    label={label('structure.deselect')}
                    onClick={deselect}
                    data-testid="structure-deselect-button"
                />
            </DetailsCardHeader>
            <StructureInformation
                roi={roi}
                volumeCm3={volumeCm3}
                isEditable={structureCanBeEdited}
                canEditGeometry={roi.canModifyContours}
                typeMenuOpen={menu?.kind === 'type'}
                onOpenTypeMenu={openMenu('type')}
                onPointCoordinatesChange={setPointCoordinates}
            />
            {children}
            <StructureMoreMenu
                roi={roi}
                anchorPosition={menuPosition('more')}
                colorAnchorEl={cardRef.current}
                openerEl={menu?.opener ?? null}
                onClose={closeMenu}
            />
            <StructureTypeMenu
                roi={roi}
                open={menu?.kind === 'type'}
                anchorReference="anchorPosition"
                anchorPosition={menuPosition('type')}
                transformOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                marginThreshold={8}
                onChange={setInterpretedType}
                onClose={closeMenu}
            />
            {propertiesOpen && (
                <StructurePropertiesPanel
                    roi={roi}
                    anchorEl={cardRef.current}
                    volumeCm3={volumeCm3}
                    equivalentSphereDiameterCm={equivalentSphereDiameterCm}
                    hasContourData={hasContourData}
                    isEditable={structureCanBeEdited}
                    onRename={setName}
                    onInterpretedTypeChange={setInterpretedType}
                    onElectronDensityChange={setElectronDensityOverride}
                    onMassDensityChange={setMassDensityOverride}
                    onClose={closeProperties}
                />
            )}
        </DetailsCard>
    );
}
